import { useMemo, Fragment, useContext } from "react"
import PropTypes from "prop-types"
import { map, omit, pick } from "lodash"
import { Divider } from "@mui/material"
import { createPeriodPerformanceSingleDateQuery, ANNUALIZED_RETURNS, ANNUALIZED_VOLATILITY, MAX_DRAWDOWN, RETURNS_CORRELATION, SHARPE_RATIO, SORTINO_RATIO, FACTORS_ANNUALIZED_RETURNS, FACTORS_ANNUALIZED_VOLATILITY, FACTORS_SHARPE_RATIO, ALPHA_ANNUALIZED_RETURNS, ALPHA_ANNUALIZED_VOLATILITY, ALPHA_SHARPE_RATIO, TOTAL_RETURN, ALPHA_RETURN, FACTORS_RETURN, TRADING_RETURN, RESIDUAL_RETURN, RESIDUAL_ANNUALIZED_RETURNS, RESIDUAL_ANNUALIZED_VOLATILITY } from "gatherers/single-date/period-performance"
import DataIntegrator from "gatherers/data-integrator"
import { percentageFormatter } from "helpers/formatters/numbers"
import CompareSection from "connected-components/compare/renderers/compare-section"
import { useAnalytics } from "connected-components/analytics"
import { useIsRfrAttributionEnabled } from "resources/account"
import { KPIContext } from "../../context/kpi"
import { KPI_EVENTS } from "../../analytics"
import { addDeltaColumn } from "connected-components/shared/helpers/data-transforms"

export default function KPIPerformance({ modelMetadata, context, instrumentsWithQueryOptions, instrumentsWithMetadata, positionSetsWithQueryOptions, positionSets, isIntradayMode, showDelta, getEffectiveEndDate }) {
  const { sectionSettings, setSectionExpanded } = useContext(KPIContext)
  const { base, performanceStartDate, isEndVsStartDateComparison } = useContext(context)
  const analytics = useAnalytics()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()

  const modelName = useMemo(() => pick(modelMetadata, ["id"]), [modelMetadata])

  const performanceSections = useMemo(
    () => [
      {
        id: "total",
        name: "Total Return",
        defaultExpanded: true,
        separator: true,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              TOTAL_RETURN,
              ANNUALIZED_RETURNS,
              ANNUALIZED_VOLATILITY,
              SHARPE_RATIO,
              SORTINO_RATIO,
              RETURNS_CORRELATION,
              MAX_DRAWDOWN,
            ],
            { contextBase: base, isIntradayMode, isRfrAttributionEnabled }),
        ),
        rowCount: 6,
      },
      {
        id: "alpha",
        name: "Alpha",
        separator: true,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              ALPHA_RETURN,
              ALPHA_ANNUALIZED_RETURNS,
              ALPHA_ANNUALIZED_VOLATILITY,
              ALPHA_SHARPE_RATIO,
            ],
            { contextBase: base, isIntradayMode, isRfrAttributionEnabled }),
        ),
        rowCount: 3,
      },
      {
        id: "factors",
        name: "Factors",
        separator: true,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              FACTORS_RETURN,
              FACTORS_ANNUALIZED_RETURNS,
              FACTORS_ANNUALIZED_VOLATILITY,
              FACTORS_SHARPE_RATIO,
            ],
            { contextBase: base, isIntradayMode, isRfrAttributionEnabled }),
        ),
        rowCount: 3,
      },
      {
        id: "tradingPnl",
        name: "Trading",
        separator: !isRfrAttributionEnabled,
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              TRADING_RETURN,
            ],
            { contextBase: base, isIntradayMode, isRfrAttributionEnabled }),
        ),
        rowCount: 0,
      },
      ...(isRfrAttributionEnabled ? [{
        id: "residual",
        name: "Residual",
        metrics: map(positionSetsWithQueryOptions, (positionSet, index) =>
          createPeriodPerformanceSingleDateQuery(
            modelName.id,
            positionSet.queryPositionSet,
            positionSet.queryBase,
            positionSetsWithQueryOptions[0].queryPositionSet,
            performanceStartDate,
            getEffectiveEndDate(index),
            [
              RESIDUAL_RETURN,
              RESIDUAL_ANNUALIZED_RETURNS,
              RESIDUAL_ANNUALIZED_VOLATILITY,
            ],
            { contextBase: base, isIntradayMode, isRfrAttributionEnabled }),
        ),
        rowCount: 2,
      }] : []),
    ],
    [positionSetsWithQueryOptions, modelName.id, performanceStartDate, getEffectiveEndDate, base, isIntradayMode, isRfrAttributionEnabled],
  )

  return map(performanceSections, (section, index) => <Fragment key={`performance-${section.id}`}>
    <DataIntegrator
      dataTransform={showDelta ? addDeltaColumn : undefined}
      metrics={section.metrics}
      Renderer={section.renderer || CompareSection}
      rendererProps={{
        queryState: sectionSettings?.[section.id] || { expanded: section.defaultExpanded, all: false },
        onQueryStateChange: (queryState) => {
          analytics.track(KPI_EVENTS.SECTION_TOGGLED, {
            sectionName: section.id,
            newState: queryState,
          })
          setSectionExpanded({ key: section.id, value: queryState })
        },
        index,
        formatter: percentageFormatter,
        configKey: `performance-${section.id}`,
        columnCount: showDelta ? 3 : positionSets.length,
        positionSetsMetadata: positionSets,
        instrumentsWithQueryOptions,
        instrumentsWithMetadata,
        isEndVsStartDateComparison,
        useSkeletonLoader: true,
        buttonSize: "small",
        ...omit(section, ["id", "metrics", "renderer"]),
      }}
    />
    {section.separator && <Divider />}
  </Fragment>)
}

KPIPerformance.propTypes = {
  modelMetadata: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    missingDataFiles: PropTypes.arrayOf(PropTypes.string),
    categories: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })),
    availability: PropTypes.shape({
      securitiesStartDate: PropTypes.string,
      currentDate: PropTypes.string,
      unmodeledDates: PropTypes.object,
    }),
  }).isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  positionSetsWithQueryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  positionSets: PropTypes.arrayOf(PropTypes.object).isRequired,
  isIntradayMode: PropTypes.bool.isRequired,
  showDelta: PropTypes.bool.isRequired,
  getEffectiveEndDate: PropTypes.func.isRequired,
}
